import { ArrowDown } from 'lucide-react'
import { useTranslationChange } from 'i18nano'
import { useCallback, useRef, useState } from 'react'
import { Button } from 'shared/components/ui/button'
import { useTranslate } from 'shared/lib/i18n'
import { useOfferDocument } from 'shared/components/public-offer/useOfferDocument'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'

export function PublicOfferDialog({
	open,
	onOpenChange,
	onAccept,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	onAccept: () => void
}) {
	const { t } = useTranslate()
	const { lang } = useTranslationChange()
	const scrollRef = useRef<HTMLDivElement>(null)
	const [hasReachedBottom, setHasReachedBottom] = useState(false)
	const { data: offer, isError } = useOfferDocument(lang, open)

	const measure = useCallback(() => {
		const container = scrollRef.current
		if (!container) return

		setHasReachedBottom(
			container.scrollTop + container.clientHeight >= container.scrollHeight - 1,
		)
	}, [])

	/** Re-measures once the lazily loaded text is in the DOM (keyed per language). */
	const contentRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (node) measure()
		},
		[measure],
	)

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className='flex h-[85dvh] max-h-[720px] flex-col gap-0 overflow-hidden p-0 sm:max-w-md'
			>
				<DialogHeader className='border-b px-4 py-4'>
					<DialogTitle className='text-base'>{t('publicOffer.title')}</DialogTitle>
				</DialogHeader>

				<div
					ref={scrollRef}
					onScroll={measure}
					className='min-h-0 flex-1 overflow-y-auto px-4 py-5'
				>
					{offer ? (
						<div
							key={lang}
							ref={contentRef}
							className='space-y-6 text-[15px] leading-6 text-foreground'
						>
							<header className='space-y-2'>
								<h2 className='text-lg font-semibold'>{offer.heading}</h2>
								<p className='font-semibold'>{offer.subheading}</p>
								<p className='text-sm text-muted-foreground'>{offer.tagline}</p>
								<dl className='space-y-1 text-sm'>
									{offer.meta.map((item) => (
										<div key={item.label} className='flex gap-2'>
											<dt className='text-muted-foreground'>{item.label}:</dt>
											<dd>{item.value}</dd>
										</div>
									))}
								</dl>
							</header>

							<p className='italic text-muted-foreground'>{offer.intro}</p>

							<p>
								<span className='font-semibold'>{offer.model.label}</span>{' '}
								{offer.model.text}
							</p>

							{offer.sections.map((section) => (
								<section key={section.title} className='space-y-3'>
									<h3 className='font-semibold'>{section.title}</h3>
									{section.paragraphs.map((paragraph) => (
										<p key={paragraph}>{paragraph}</p>
									))}
								</section>
							))}

							<footer className='space-y-1 border-t pt-4 text-sm'>
								{offer.footer.map((item) => (
									<p key={item.label}>
										<span className='font-semibold'>{item.label}</span> {item.value}
									</p>
								))}
							</footer>
						</div>
					) : (
						<p className='text-[15px] leading-6 text-muted-foreground'>
							{isError ? t('publicOffer.loadFailed') : t('publicOffer.loading')}
						</p>
					)}
				</div>

				<div className='border-t px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]'>
					<p className='mb-3 text-sm leading-5 text-muted-foreground'>
							{t('publicOffer.acceptNotice')}
					</p>
					<Button
						type='button'
						size='lg'
						className='h-11 w-full text-base font-semibold'
						disabled={!offer || !hasReachedBottom}
						onClick={onAccept}
					>
						{hasReachedBottom ? (
							t('publicOffer.accept')
						) : (
							<>
								{t('publicOffer.scrollDown')}
								<ArrowDown aria-hidden='true' />
							</>
						)}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
